/**
 * Custom column computation utilities
 * Handles formula operations and numeric value extraction
 */

import {
  FormulaOperation,
  CustomColumn,
  DimensionType,
} from '@/types/rebateAnalytics';
import type { BreakdownCell } from '@/components/ui/breakdown-tables/types';
import {
  buildColumnReferenceMap,
  resolveColumnReference,
} from './columnReferences';

export type CellValue =
  | number
  | { current: number; previous: number; delta: number }
  | null;

/**
 * Extract numeric value from cell value (handles YOY objects)
 */
export function extractNumericValue(value: CellValue): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object' && 'current' in value) {
    return value.current;
  }
  return value as number;
}

/**
 * Compute the result of a formula operation
 */
export function computeOperation(
  left: CellValue,
  right: CellValue,
  operation: FormulaOperation,
): number | null {
  // Extract numeric values (for YOY cells, use current value)
  const leftNum = extractNumericValue(left);
  const rightNum = extractNumericValue(right);

  // If either operand is null/undefined, return null (displays as '-')
  if (
    leftNum === null ||
    leftNum === undefined ||
    rightNum === null ||
    rightNum === undefined
  ) {
    return null;
  }

  switch (operation) {
    case FormulaOperation.ADD:
      return leftNum + rightNum;
    case FormulaOperation.SUBTRACT:
      return leftNum - rightNum;
    case FormulaOperation.MULTIPLY:
      return leftNum * rightNum;
    case FormulaOperation.DIVIDE:
      // Handle division by zero
      if (rightNum === 0) {
        return null;
      }
      return leftNum / rightNum;
    default:
      return null;
  }
}

/**
 * Get cell value from BreakdownCell, handling YOY objects
 */
function getCellValue(cell: BreakdownCell | undefined): CellValue {
  if (!cell) return null;
  if (cell.value === null || cell.value === undefined) return null;
  if (typeof cell.value === 'number') return cell.value;
  if (
    typeof cell.value === 'object' &&
    'current' in cell.value &&
    'previous' in cell.value &&
    'delta' in cell.value &&
    typeof (cell.value as { current: number; previous: number; delta: number })
      .current === 'number'
  ) {
    return cell.value as { current: number; previous: number; delta: number };
  }
  return null;
}

/**
 * Find a cell in the cells array by its coordinates
 */
function findCell(
  cells: BreakdownCell[],
  column: string,
  subColumn: string | null,
  row: string,
  subRow: string | null,
): BreakdownCell | undefined {
  return cells.find(
    (cell) =>
      cell.column === column &&
      cell.subColumn === subColumn &&
      cell.row === row &&
      cell.subRow === subRow,
  );
}

/**
 * Add custom calculated columns to BreakdownCell[] data
 */
export function addCustomColumns({
  cells,
  customColumns,
  secondaryXDimension,
}: {
  cells: BreakdownCell[];
  customColumns: CustomColumn[];
  secondaryXDimension?: DimensionType;
}): BreakdownCell[] {
  if (!customColumns || customColumns.length === 0) {
    return cells;
  }

  // Get all unique column names (primary columns, not sub-columns)
  const uniqueColumns = new Set<string>();
  cells.forEach((cell) => {
    uniqueColumns.add(cell.column);
  });

  // Build column reference map from existing columns
  const columnRefMap = buildColumnReferenceMap(
    Array.from(uniqueColumns),
    secondaryXDimension,
  );

  const newCells: BreakdownCell[] = [...cells];

  // Process each custom column
  customColumns.forEach((customCol) => {
    // Resolve left operand
    const leftRef = resolveColumnReference(customCol.leftOperand, columnRefMap);

    // Resolve right operand
    const rightRef =
      typeof customCol.rightOperand === 'number'
        ? customCol.rightOperand
        : resolveColumnReference(
            customCol.rightOperand as string,
            columnRefMap,
          );

    if (!leftRef) {
      // Invalid reference, skip this custom column
      return;
    }

    // Get all unique row combinations
    const uniqueRows = new Set<string>();
    const rowToSubRows = new Map<string, Set<string | null>>();
    cells.forEach((cell) => {
      const rowKey = cell.row;
      uniqueRows.add(rowKey);
      if (!rowToSubRows.has(rowKey)) {
        rowToSubRows.set(rowKey, new Set());
      }
      rowToSubRows.get(rowKey)!.add(cell.subRow ?? null);
    });

    // Process each row
    uniqueRows.forEach((row) => {
      const subRows = rowToSubRows.get(row) || new Set([null]);

      subRows.forEach((subRow) => {
        // Handle vectorized operations (when leftRef is an array)
        if (Array.isArray(leftRef)) {
          // Vectorized: compute across matching sub-dimensions
          const rightHeaders = Array.isArray(rightRef) ? rightRef : [];

          // Get union of all sub-dimensions from left and right
          const leftSecondaryValues = leftRef
            .map((h) => {
              const parts = h.split(' - ');
              return parts.length > 1 ? parts[1] : null;
            })
            .filter((v): v is string => v !== null);

          const rightSecondaryValues = rightHeaders
            .map((h) => {
              const parts = h.split(' - ');
              return parts.length > 1 ? parts[1] : null;
            })
            .filter((v): v is string => v !== null);

          const allSecondaryValues = new Set([
            ...leftSecondaryValues,
            ...rightSecondaryValues,
          ]);

          // Create computed sub-column for each dimension
          allSecondaryValues.forEach((secondaryValue) => {
            const customColumnName = `${customCol.name} - ${secondaryValue}`;

            // Find matching left and right cells for this secondary value
            const leftCell = leftRef.find((h) =>
              h.includes(` - ${secondaryValue}`),
            );
            const rightCellValue =
              typeof rightRef === 'number'
                ? rightRef
                : Array.isArray(rightRef)
                  ? rightRef.find((h) => h.includes(` - ${secondaryValue}`))
                  : rightRef;

            if (!leftCell) return;

            const leftCellValue = getCellValue(
              findCell(
                cells,
                leftCell.split(' - ')[0],
                secondaryValue,
                row,
                subRow,
              ),
            );

            const rightCellValueResolved: CellValue =
              typeof rightCellValue === 'string'
                ? (getCellValue(
                    findCell(
                      cells,
                      rightCellValue.split(' - ')[0],
                      secondaryValue,
                      row,
                      subRow,
                    ),
                  ) ?? null)
                : (rightCellValue ?? null);

            const result = computeOperation(
              leftCellValue,
              rightCellValueResolved,
              customCol.operation,
            );

            if (result !== null) {
              newCells.push({
                column: customCol.name,
                subColumn: secondaryValue,
                row,
                subRow,
                value: result,
                metadata: {
                  isCustomColumn: true,
                  customColumnId: customCol.id,
                },
              });
            }
          });
        } else {
          // Scalar operation: single column reference
          const leftColumn = leftRef as string;
          const leftParts = leftColumn.split(' - ');
          const leftPrimaryColumn = leftParts[0];
          const leftSubColumn = leftParts.length > 1 ? leftParts[1] : null;

          const leftCellValue = getCellValue(
            findCell(cells, leftPrimaryColumn, leftSubColumn, row, subRow),
          );

          let rightCellValue: CellValue;
          if (typeof customCol.rightOperand === 'number') {
            rightCellValue = customCol.rightOperand;
          } else if (typeof rightRef === 'string') {
            const rightParts = rightRef.split(' - ');
            const rightPrimaryColumn = rightParts[0];
            const rightSubColumn = rightParts.length > 1 ? rightParts[1] : null;
            rightCellValue = getCellValue(
              findCell(cells, rightPrimaryColumn, rightSubColumn, row, subRow),
            );
          } else {
            return; // Invalid right operand
          }

          const result = computeOperation(
            leftCellValue,
            rightCellValue,
            customCol.operation,
          );

          if (result !== null) {
            newCells.push({
              column: customCol.name,
              subColumn: null,
              row,
              subRow,
              value: result,
              metadata: {
                isCustomColumn: true,
                customColumnId: customCol.id,
              },
            });
          }
        }
      });
    });
  });

  return newCells;
}
